import * as React from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import type { Category } from "../../models/Category";
import { countDescendants } from "./categoryTree.utils";
import { getApiErrorMessage } from "./apiError.utils";

type CategoryDeleteDialogProps = {
    category: Category | null;
    categories: Category[];
    onCancel: () => void;
    onConfirm: () => Promise<void>;
};

/**
 * Mirrors LocationDeleteDialog. I don't have the categories controller to
 * confirm it enforces the same "no children / not in use" rules as
 * locations does — if it doesn't block on children server-side, this
 * client-side check is still a reasonable safety net for keeping the
 * hierarchy consistent, but worth verifying against the real endpoint.
 */
export default function CategoryDeleteDialog({ category, categories, onCancel, onConfirm }: CategoryDeleteDialogProps) {
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");
    const descendantCount = category ? countDescendants(categories, category.id) : 0;
    const blockedByChildren = descendantCount > 0;

    React.useEffect(() => {
        setDeleteError("");
        setDeleting(false);
    }, [category?.id]);

    const handleConfirm = async () => {
        setDeleting(true);
        setDeleteError("");
        try {
            await onConfirm();
        } catch (e: any) {
            setDeleteError(getApiErrorMessage(e, "Unable to delete this category."));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={category != null} onClose={deleting ? undefined : onCancel}>
            <DialogTitle>Remove "{category?.name}"?</DialogTitle>
            <DialogContent>
                {blockedByChildren ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        This category has {descendantCount} sub-categor{descendantCount === 1 ? "y" : "ies"} beneath
                        it and can't be deleted until they're moved or removed first.
                    </Alert>
                ) : (
                    <DialogContentText>This can't be undone.</DialogContentText>
                )}
                {deleteError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {deleteError}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={deleting}>
                    {blockedByChildren ? "Close" : "Cancel"}
                </Button>
                {!blockedByChildren && (
                    <Button color="error" variant="contained" onClick={handleConfirm} disabled={deleting}>
                        Remove
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
